import React, { useEffect, useState } from "react";
import { Alert, Button, Group, List, Modal, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconBan } from "@tabler/icons-react";
import { useApp } from "../../../context/AppContext";
import { useResponsive } from "../../../context/ResponsiveContext";
import { VoidTransactionPreviewResponse } from "../../../types/transactions";
import { notifySuccess } from "../../../utils/notify";
import BrandedLoadingOverlay from "../../common/BrandedLoadingOverlay";
import styles from "./sales.module.css";

interface VoidTransactionModalProps {
  opened: boolean;
  onClose: () => void;
  businessLocationId: string;
  /** `transaction.id` of the sales row. */
  transactionId: string;
  /** Human-facing reference, shown in the copy. */
  referenceId?: string;
  onSuccess: () => void | Promise<void>;
}

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

/**
 * Void a sales transaction. On open it asks the backend for a preview verdict
 * (`{preview: true}`, no mutation); a blocked verdict shows `block_reason` with
 * only a Close action, otherwise the warnings (verbatim) and a destructive
 * confirm. The rules live server-side; nothing here re-derives them.
 */
export default function VoidTransactionModal({
  opened,
  onClose,
  businessLocationId,
  transactionId,
  referenceId,
  onSuccess,
}: VoidTransactionModalProps) {
  const { isMobile } = useResponsive();
  const { voidTransaction } = useApp();

  const [preview, setPreview] = useState<VoidTransactionPreviewResponse | null>(
    null,
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    let cancelled = false;
    setPreview(null);
    setPreviewError(null);
    setError(null);
    setIsSubmitting(false);
    setPreviewLoading(true);
    (async () => {
      try {
        const resp = await voidTransaction(businessLocationId, transactionId, {
          preview: true,
        });
        if (!cancelled) setPreview(resp);
      } catch (err) {
        if (!cancelled) {
          setPreviewError(
            errorMessage(err, "Couldn't check this transaction. Try again."),
          );
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, businessLocationId, transactionId, voidTransaction]);

  const handleVoid = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await voidTransaction(businessLocationId, transactionId, {});
      notifySuccess(
        referenceId
          ? `Transaction #${referenceId} has been voided.`
          : "The transaction has been voided.",
        { title: "Transaction voided" },
      );
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in void success handler:", e);
      }
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Failed to void the transaction."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const blocked = preview !== null && !preview.can_void;
  const canConfirm = preview !== null && preview.can_void;
  const warnings = preview?.warnings ?? [];
  const buttonSize = isMobile ? "md" : "sm";
  // Sales has no mobile layout; the modal goes full screen with 44px targets.
  const touchClass = isMobile ? styles.touchTarget : undefined;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Void transaction</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <BrandedLoadingOverlay visible={previewLoading || isSubmitting} />

      <Stack gap="md">
        {previewError && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red">
            {previewError}
          </Alert>
        )}

        {blocked && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red">
            {preview?.block_reason || "This transaction can't be voided."}
          </Alert>
        )}

        {canConfirm && (
          <>
            {warnings.length > 0 ? (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="var(--color-warning)"
                variant="light"
              >
                <List size="sm" spacing={4}>
                  {warnings.map((w, i) => (
                    <List.Item key={`${i}-${w}`}>{w}</List.Item>
                  ))}
                </List>
              </Alert>
            ) : (
              <Text size="sm">
                {referenceId
                  ? `Void transaction #${referenceId}? It will be excluded from all reports.`
                  : "Void this transaction? It will be excluded from all reports."}
              </Text>
            )}
          </>
        )}

        {error && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red">
            {error}
          </Alert>
        )}

        <Group justify="flex-end">
          {canConfirm ? (
            <>
              <Button
                variant="default"
                size={buttonSize}
                className={touchClass}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                color="var(--color-danger)"
                size={buttonSize}
                className={touchClass}
                onClick={handleVoid}
                loading={isSubmitting}
                leftSection={<IconBan size={18} />}
              >
                Void transaction
              </Button>
            </>
          ) : (
            <Button
              variant="default"
              size={buttonSize}
              className={touchClass}
              onClick={onClose}
              disabled={previewLoading}
            >
              Close
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
